import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useWorkspaceUserCountQuery } from '@treams/treams-network';
import {
	Button,
	Card,
	Checkbox,
	CircleCheckmarkIcon,
	Container,
	FlexGap,
	Heading3,
	NewBadgeIcon,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	Pill,
	Subheading,
	Title,
	UnderlineLink,
	tokens,
} from '@treams/treams-ui';

const UNIT_PRICE_EUR = 1.79;

const FEATURES = ['ask', 'feedback', 'goals', 'oneOnOnes', 'recordings'] as const;

const euro = new Intl.NumberFormat('nl-NL', {
	style: 'currency',
	currency: 'EUR',
});

function formatEuro(amount: number) {
	return euro.formatToParts(amount).reduce((value, part) => {
		if (part.type === 'literal' && part.value.trim() === '') {
			return value;
		}

		return value + part.value;
	}, '');
}

export function EnableAiPlan(props: {
	workspaceName: string;
	isWorkspaceSuperuser: boolean;
	enabling: boolean;
	onEnable: () => void;
}) {
	const { t } = useTranslation();
	const [accepted, setAccepted] = useState(false);
	const { data: userCountData, loading: countingUsers } = useWorkspaceUserCountQuery({
		skip: !props.isWorkspaceSuperuser,
	});

	const userCount = userCountData?.workspaceUserCount;
	const unitPrice = formatEuro(UNIT_PRICE_EUR);
	const monthlyTotal =
		countingUsers || userCount === undefined ? '—' : formatEuro(userCount * UNIT_PRICE_EUR);
	const userCountLabel = countingUsers || userCount === undefined ? '—' : String(userCount);

	const toggleAccepted = () => setAccepted((current) => !current);

	return (
		<Container py={4} wide={PAGE_CONTENT_WIDTH}>
			<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
				<FlexGap alignItems="center" gap={tokens.gap.group}>
					<Title>{t('assistant.enablePlan.title')}</Title>
					<Pill tint="warning">{t('assistant.enablePlan.statusInactive')}</Pill>
				</FlexGap>
				<Paragraph maxWidth={800}>{t('assistant.enablePlan.intro')}</Paragraph>
			</FlexGap>

			<Card my={4}>
				<FlexGap flexDirection="column" gap={tokens.gap.group} width="100%">
					<Heading3>
						{t('assistant.enablePlan.enableTitle', {
							workspaceName: props.workspaceName,
						})}
					</Heading3>
					<FlexGap flexDirection="column" gap={tokens.gap.list} width="100%">
						<Paragraph>
							<Trans
								i18nKey="assistant.enablePlan.price"
								values={{
									unitPrice,
									userCount: userCountLabel,
									monthlyTotal,
								}}
								components={{ strong: <strong /> }}
							/>
						</Paragraph>
						<Paragraph>{t('assistant.enablePlan.invoice')}</Paragraph>
					</FlexGap>
					{props.isWorkspaceSuperuser && (
						<>
							<FlexGap alignItems="flex-start" gap={tokens.gap.element} width="100%">
								<Box flexShrink={0} pt="3px">
									<Checkbox checked={accepted} onClick={toggleAccepted} />
								</Box>
								<Paragraph
									flex={1}
									minWidth={0}
									style={{ cursor: 'pointer' }}
									onClick={(event) => {
										if ((event.target as HTMLElement).closest('a')) {
											return;
										}

										toggleAccepted();
									}}
								>
									<Trans
										i18nKey="assistant.enablePlan.terms"
										values={{ workspaceName: props.workspaceName }}
										components={{
											terms: (
												<UnderlineLink
													href={t('assistant.enablePlan.termsLink')}
													target="_blank"
													rel="noreferrer"
												/>
											),
											privacy: (
												<UnderlineLink
													href={t('assistant.enablePlan.privacyLink')}
													target="_blank"
													rel="noreferrer"
												/>
											),
										}}
									/>
								</Paragraph>
							</FlexGap>
							<Button
								maxWidth="180px"
								icon={<NewBadgeIcon size={16} />}
								title={t('assistant.enablePlan.enableButton')}
								disabled={!accepted || props.enabling}
								loading={props.enabling}
								disableSuccess
								alignment="start"
								onClick={props.onEnable}
							/>
						</>
					)}
				</FlexGap>
			</Card>

			<Card>
				<FlexGap flexDirection="column" gap={tokens.gap.list} flex="1 1 360px" minWidth={0}>
					<Heading3>{t('assistant.enablePlan.featuresTitle')}</Heading3>
					{FEATURES.map((feature) => (
						<FlexGap key={feature} alignItems="flex-start" gap={tokens.gap.element} width="100%">
							<CircleCheckmarkIcon size={20} color={tokens.success.icon} />
							<FlexGap flexDirection="column" gap={tokens.gap.inline} flex="1 1 auto">
								<Subheading>{t(`assistant.enablePlan.features.${feature}.title`)}</Subheading>
								<Paragraph small>
									{t(`assistant.enablePlan.features.${feature}.description`)}
								</Paragraph>
							</FlexGap>
						</FlexGap>
					))}
				</FlexGap>
			</Card>
		</Container>
	);
}
